import { Skeleton } from "@/components/ui/skeleton";
import type { HttpTypes } from "@medusajs/types";
import { Checkbox, Label } from "@medusajs/ui";
import { cn } from "@repo/design-system/utils";
import { memo, useCallback, useState } from "react";
import type { UseFormReturn } from "react-hook-form";
import { Controller, useWatch } from "react-hook-form";
import { AddressDialog } from "@/components/addresses";
import type {
  CheckoutAddressFormData,
  InlineAddressData
} from "../validations";
import { AddressDropdown } from "./AddressDropdown";
import { CheckoutAddressDialog } from "./CheckoutAddressDialog";

export interface BillingAddressSectionProps {
  /** React Hook Form instance */
  form: UseFormReturn<CheckoutAddressFormData>;
  /** Customer addresses */
  addresses: HttpTypes.StoreCustomerAddress[];
  /** Whether addresses are loading */
  isLoading?: boolean;
  /** Whether the form is disabled (e.g., during submission) */
  disabled?: boolean;
  /**
   * Callback when billing address changes - used for real-time cart updates
   */
  onAddressChange?: (
    address: HttpTypes.StoreCustomerAddress | InlineAddressData
  ) => void;
}

/**
 * Billing address section for checkout
 *
 * Features:
 * - "Same as shipping address" checkbox toggle
 * - Collapse/expand animation when toggling
 * - Address dropdown for billing addresses
 * - Add new address dialog with "save for later" option
 * - Supports both saved and one-time addresses
 *
 * @example
 * ```tsx
 * <BillingAddressSection
 *   form={form}
 *   addresses={addresses}
 *   isLoading={isLoading}
 * />
 * ```
 */
export const BillingAddressSection = memo(function BillingAddressSection({
  form,
  addresses,
  isLoading = false,
  disabled = false,
  onAddressChange
}: BillingAddressSectionProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAddress, setEditingAddress] =
    useState<HttpTypes.StoreCustomerAddress | null>(null);

  const {
    formState: { errors },
    setValue,
    control
  } = form;

  // useWatch triggers re-renders when values change (works with memo)
  const sameAsShipping = useWatch({ control, name: "sameAsShipping" });
  const billingAddressId = useWatch({ control, name: "billingAddressId" });
  const billingAddress = useWatch({ control, name: "billingAddress" });

  // Handle clearing billing address when "same as shipping" is checked
  const handleSameAsShippingChange = useCallback(
    (checked: boolean, fieldOnChange: (value: boolean) => void) => {
      // Update the form field via Controller
      fieldOnChange(checked);

      if (checked) {
        // Clear billing address selection when using same as shipping
        setValue("billingAddressId", "", { shouldValidate: false });
        setValue("billingAddress", undefined, { shouldValidate: false });
      }
    },
    [setValue]
  );

  // Handle saved address selection
  const handleSelectAddress = useCallback(
    (addressId: string) => {
      // Clear any custom address when selecting a saved one
      setValue("billingAddress", undefined, { shouldValidate: false });
      setValue("billingAddressId", addressId, { shouldValidate: true });

      // Trigger cart update
      const selectedAddress = addresses.find((a) => a.id === addressId);
      if (selectedAddress && onAddressChange) {
        onAddressChange(selectedAddress);
      }
    },
    [addresses, setValue, onAddressChange]
  );

  // Handle custom address re-selection (from dropdown)
  const handleSelectCustom = useCallback(() => {
    // Clear saved address ID to use custom address
    setValue("billingAddressId", "", { shouldValidate: false });
  }, [setValue]);

  // Handle add new address
  const handleAddNewAddress = useCallback(() => {
    setDialogOpen(true);
  }, []);

  // Handle dialog close
  const handleDialogClose = useCallback((open: boolean) => {
    setDialogOpen(open);
  }, []);

  // Handle edit existing address
  const handleEditAddress = useCallback(
    (address: HttpTypes.StoreCustomerAddress) => {
      setEditingAddress(address);
    },
    []
  );

  // Handle edit dialog close
  const handleEditDialogClose = useCallback((open: boolean) => {
    if (!open) {
      setEditingAddress(null);
    }
  }, []);

  // Handle edit success - refresh the cart address if it's the selected one
  const handleEditSuccess = useCallback(
    (updatedAddress?: HttpTypes.StoreCustomerAddress) => {
      if (updatedAddress && onAddressChange) {
        onAddressChange(updatedAddress);
      }
    },
    [onAddressChange]
  );

  // Handle address creation/entry success
  const handleAddressSuccess = useCallback(
    (result: {
      savedAddressId?: string;
      inlineAddress?: InlineAddressData;
    }) => {
      if (result.savedAddressId) {
        // Saved address - select it by ID
        setValue("billingAddress", undefined, { shouldValidate: false });
        setValue("billingAddressId", result.savedAddressId, {
          shouldValidate: true
        });

        // Trigger cart update
        const selectedAddress = addresses.find(
          (a) => a.id === result.savedAddressId
        );
        if (selectedAddress && onAddressChange) {
          onAddressChange(selectedAddress);
        }
      } else if (result.inlineAddress) {
        // One-time address - store inline data
        setValue("billingAddressId", "", { shouldValidate: false });
        setValue("billingAddress", result.inlineAddress, {
          shouldValidate: true
        });

        // Trigger cart update
        if (onAddressChange) {
          onAddressChange(result.inlineAddress);
        }
      }
    },
    [addresses, setValue, onAddressChange]
  );

  // Loading skeleton state
  if (isLoading) {
    return (
      <div
        className="space-y-4"
        role="status"
        aria-label="Loading billing address options"
      >
        {/* Checkbox skeleton */}
        <div className="flex items-start gap-3">
          <Skeleton className="h-5 w-5 rounded" />
          <div className="space-y-1">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-3 w-52" />
          </div>
        </div>
        {/* Address dropdown skeleton */}
        <div className="pt-2">
          <Skeleton className="mb-3 h-4 w-28" />
          <Skeleton className="h-10 w-full rounded-md" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Same as shipping checkbox */}
      <div className="flex items-start gap-3">
        <Controller
          name="sameAsShipping"
          control={control}
          render={({ field }) => (
            <Checkbox
              id="same-as-shipping"
              checked={field.value}
              onCheckedChange={(checked) => {
                // Handle Radix checkbox which can return boolean or "indeterminate"
                const isChecked = checked === true;
                handleSameAsShippingChange(isChecked, field.onChange);
              }}
              disabled={disabled || isLoading}
              aria-describedby="same-as-shipping-description"
            />
          )}
        />
        <div className="space-y-1">
          <Label
            htmlFor="same-as-shipping"
            className="cursor-pointer text-sm font-medium"
          >
            Same as shipping address
          </Label>
          <p
            id="same-as-shipping-description"
            className="text-sm text-gray-500"
          >
            Use the shipping address for billing
          </p>
        </div>
      </div>

      {/* Billing address selector - with collapse animation */}
      <div
        className={cn(
          "grid transition-all duration-200 ease-in-out",
          sameAsShipping
            ? "grid-rows-[0fr] opacity-0"
            : "grid-rows-[1fr] opacity-100"
        )}
        aria-hidden={sameAsShipping}
      >
        <div className="-m-px overflow-hidden p-px">
          <div className="pt-2">
            {/* <h3 className="text-sm font-medium text-gray-900 mb-3">
              Billing Address <span className="text-red-500">*</span>
            </h3> */}
            <AddressDropdown
              type="billing"
              addresses={addresses}
              selectedId={billingAddressId || null}
              customAddress={billingAddress}
              onSelect={handleSelectAddress}
              onSelectCustom={handleSelectCustom}
              onAddNew={handleAddNewAddress}
              onEdit={handleEditAddress}
              disabled={disabled || sameAsShipping}
              isLoading={isLoading}
              error={
                !sameAsShipping ? errors.billingAddressId?.message : undefined
              }
              placeholder="Select a billing address"
            />
          </div>
        </div>
      </div>

      {/* Add Address Dialog */}
      <CheckoutAddressDialog
        open={dialogOpen}
        onOpenChange={handleDialogClose}
        type="billing"
        isFirstAddress={addresses.length === 0}
        onSuccess={handleAddressSuccess}
      />

      {/* Edit Address Dialog */}
      <AddressDialog
        open={!!editingAddress}
        onOpenChange={handleEditDialogClose}
        address={editingAddress}
        onSuccess={handleEditSuccess}
      />
    </div>
  );
});
